import { Button, Card, Form, Input, Space } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { PageAlert as Alert } from "@/views/components/PageAlert";
import { formatMoney } from "@/utils/money";
import { pickReservationTransportDisplay } from "@/views/pages/tradeManage/zoneReservationOrder/zoneReservationDisplay";
import type { SpecialZoneReservationOrderUpdateParams } from "@zhihaoscm/entity";
import {
    BaseItem as ZhBaseItem,
    DatePicker as ZhDatePicker,
    Grid as ZhGrid,
    GridItem as ZhGridItem,
    MultiGrid as ZhMultiGrid
} from "zhihao-ui-react";
import {
    getAvailableBalance,
    getContractDateRange,
    getContractTotalTon,
    getZoneLabel,
    loadZoneReservationOrderDetail,
    reservationEditTime,
    reservationMaterialName,
    reservationMaterialPreemptPrice,
    updateZoneReservationOrder,
    type DetailData
} from "./ZoneReservationOrderDetailPage";

type ReservationEditFields = {
    shipName: string;
    mobile?: string;
    reservationTime: string;
    destinationAddress: string;
    destinationPort?: string;
};

export interface ZoneReservationOrderEditPageProps {
    loadDetail?: (id: string) => Promise<DetailData>;
    updateReservation?: (id: string, params: SpecialZoneReservationOrderUpdateParams) => Promise<unknown>;
}

/** 专区预约单独立编辑页：保留既有编辑字段和接口契约，仅把承载方式从弹窗改为路由页面。 */
export function ZoneReservationOrderEditPage({
    loadDetail = loadZoneReservationOrderDetail,
    updateReservation = updateZoneReservationOrder
}: ZoneReservationOrderEditPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const reservationId = new URLSearchParams(location.search).get("id") || "";
    const [form] = Form.useForm<ReservationEditFields>();
    const [detail, setDetail] = useState<DetailData | null>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    /** 通过详情接口加载合同、物料和船舶字段，并按原编辑弹窗规则回显。 */
    useEffect(() => {
        if (!reservationId) {
            setErrorMessage("缺少预约单编号");
            setLoading(false);
            return;
        }
        let disposed = false;
        setLoading(true);
        void loadDetail(reservationId)
            .then(result => {
                if (disposed) return;
                const order = result.specialZoneReservationOrder;
                const ship = result.ship;
                setDetail(result);
                form.setFieldsValue({
                    shipName: order.shipName || ship?.cnname || ship?.name || "",
                    mobile: order.mobile || "",
                    reservationTime: reservationEditTime(order.reservationTime),
                    destinationAddress: order.destinationAddress || "",
                    destinationPort: order.destinationPort || ""
                });
                setErrorMessage(null);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("预约单详情加载失败");
            })
            .finally(() => {
                if (!disposed) setLoading(false);
            });
        return () => {
            disposed = true;
        };
    }, [form, loadDetail, reservationId]);

    /** 按原 update_1 字段契约保存，成功后返回当前预约单详情页。 */
    const submitEdit = async () => {
        if (!detail || !reservationId) return;
        const order = detail.specialZoneReservationOrder;
        try {
            const fields = await form.validateFields();
            setSubmitting(true);
            await updateReservation(reservationId, {
                shipId: order.shipId || "",
                shipName: fields.shipName.trim(),
                mobile: fields.mobile?.trim() || undefined,
                destinationAddressAreaId: order.destinationAddressAreaId || "",
                destinationAddress: fields.destinationAddress.trim(),
                destinationPort: fields.destinationPort?.trim() || undefined,
                goodsForms: (detail.specialZoneReservationOrderGoods || []).map(goods => ({
                    goodsCode: Number(goods.goodsCode),
                    plannedTonnage: Number(goods.plannedTonnage) || 0,
                    unitPrice: Number(goods.unitPrice)
                })),
                reservationTime: fields.reservationTime,
                requirementPlatId: order.requirementPlatId || undefined,
                transportOrderShipId: order.transportOrderShipId || undefined
            });
            navigate(`/tradeManage/zoneReservationOrderDetail?id=${encodeURIComponent(reservationId)}`);
        } catch (error) {
            // 表单校验错误由 Form.Item 就地展示；网络错误保留在页面顶部便于继续修改。
            if (error && typeof error === "object" && "errorFields" in error) return;
            setErrorMessage("预约单编辑失败");
        } finally {
            setSubmitting(false);
        }
    };

    const order = detail?.specialZoneReservationOrder;
    return (
        <section aria-label="zone-reservation-order-edit-page" className="flex h-full min-h-full flex-col">
            <Card loading={loading}>
                <h1 className="mb-5 text-base font-semibold">编辑预约单</h1>
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                {detail ? (
                    <>
                        <section aria-label="合同信息" className="mb-6">
                            <div className="mb-3 border-l-2 border-primary pl-2 text-sm font-semibold">合同信息</div>
                            <ZhMultiGrid>
                                <ZhGrid cols={2} gap={[32, 16]}>
                                    <ZhBaseItem label="合同吨数" value={getContractTotalTon(detail)} />
                                    <ZhBaseItem label="专区名称" value={getZoneLabel(detail)} />
                                    <ZhBaseItem label="合同日期" value={getContractDateRange(detail)} />
                                    <ZhBaseItem label="可预约金额" value={getAvailableBalance(detail)} />
                                </ZhGrid>
                            </ZhMultiGrid>
                        </section>

                        <section aria-label="物料信息" className="mb-6">
                            <div className="mb-3 border-l-2 border-primary pl-2 text-sm font-semibold">物料信息</div>
                            <ZhMultiGrid>
                                {detail.specialZoneReservationOrderGoods?.length ? (
                                    detail.specialZoneReservationOrderGoods.map((goods, index) => (
                                        <ZhGrid key={`${goods.id || goods.goodsCode}-${index}`} cols={2} gap={[32, 16]}>
                                            <ZhGridItem>
                                                <ZhBaseItem
                                                    label="物料名称"
                                                    value={reservationMaterialName(goods.goodsCode)}
                                                />
                                            </ZhGridItem>
                                            <ZhGridItem>
                                                <ZhBaseItem label="单价" value={`${formatMoney(goods.unitPrice)} 元`} />
                                            </ZhGridItem>
                                            <ZhGridItem>
                                                <ZhBaseItem
                                                    label="计划提货量"
                                                    value={`${Number(goods.plannedTonnage || 0).toLocaleString("zh-CN")} 吨`}
                                                />
                                            </ZhGridItem>
                                            <ZhGridItem>
                                                <ZhBaseItem
                                                    label="预占金额"
                                                    value={formatMoney(reservationMaterialPreemptPrice(goods))}
                                                />
                                            </ZhGridItem>
                                        </ZhGrid>
                                    ))
                                ) : (
                                    <ZhGrid cols={2} gap={[32, 16]}>
                                        <ZhBaseItem label="物料名称" value="--" />
                                        <ZhBaseItem label="单价" value="--" />
                                        <ZhBaseItem label="计划提货量" value="--" />
                                        <ZhBaseItem label="预占金额" value="--" />
                                    </ZhGrid>
                                )}
                            </ZhMultiGrid>
                        </section>

                        <section aria-label="船舶信息" className="mb-12">
                            <div className="mb-3 border-l-2 border-primary pl-2 text-sm font-semibold">船舶信息</div>
                            <Form form={form} layout="vertical">
                                <ZhMultiGrid>
                                    <ZhGrid cols={2} gap={[32, 0]}>
                                        <ZhGridItem>
                                            <ZhBaseItem
                                                label="关联船运需求/船运单"
                                                value={
                                                    pickReservationTransportDisplay(
                                                        order?.transportOrderShipId,
                                                        order?.requirementPlatId
                                                    ) || "--"
                                                }
                                            />
                                        </ZhGridItem>
                                        <ZhGridItem>
                                            <Form.Item
                                                label="船名"
                                                name="shipName"
                                                rules={[{ required: true, message: "请输入船名" }]}
                                            >
                                                <Input />
                                            </Form.Item>
                                        </ZhGridItem>
                                        <ZhGridItem>
                                            <Form.Item label="联系方式" name="mobile">
                                                <Input />
                                            </Form.Item>
                                        </ZhGridItem>
                                        <ZhGridItem>
                                            <Form.Item
                                                label="目的地"
                                                name="destinationAddress"
                                                rules={[{ required: true, message: "请输入目的地" }]}
                                            >
                                                <Input />
                                            </Form.Item>
                                        </ZhGridItem>
                                        <ZhGridItem>
                                            <Form.Item label="目的港" name="destinationPort">
                                                <Input />
                                            </Form.Item>
                                        </ZhGridItem>
                                        <ZhGridItem>
                                            <Form.Item
                                                label="预到日期"
                                                name="reservationTime"
                                                rules={[{ required: true, message: "请输入预到日期" }]}
                                            >
                                                <ZhDatePicker
                                                    aria-label="预到日期"
                                                    placeholder="请选择预到日期"
                                                    type="date"
                                                    valueFormat="YYYY-MM-DD"
                                                />
                                            </Form.Item>
                                        </ZhGridItem>
                                    </ZhGrid>
                                </ZhMultiGrid>
                            </Form>
                        </section>
                    </>
                ) : null}
                <Space className="operation-fixed-btn" size={0}>
                    <Button
                        aria-label="保存"
                        disabled={!detail}
                        loading={submitting}
                        type="primary"
                        onClick={submitEdit}
                    >
                        保存
                    </Button>
                    <Button aria-label="返回" onClick={() => navigate(-1)}>
                        返回
                    </Button>
                </Space>
            </Card>
        </section>
    );
}

export default ZoneReservationOrderEditPage;
